/**
 * Returns a function, that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * N milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing.
 *
 * @param {Function} callback - function.
 * @param {number} wait - wait time in milliseconds.
 * @returns {Function} function
 */
export const debounce = (callback, wait = 100) => {
    let timeout;

    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            callback.apply(this, args);
        }, wait);
    };
};

/**
 * Based on throttle from UnderscoreJS.
 *
 * @see http://underscorejs.org/docs/underscore.html
 * @param {Function} callback The function to throttle.
 * @param {number} wait The minimum time to wait before executing the callback (again).
 * @returns {Function} function
 */
export const throttle = (callback, wait) => {
    let timer = null;

    return function (...args) {
        if (timer === null) {
            timer = setTimeout(() => {
                callback.apply(this, args);
                timer = null;
            }, wait);
        }
    };
};

/**
 * Creates a function that invokes func with partials prepended to the arguments it receives.
 *
 * @param {Function} func - function.
 * @param  {...any} args - arguments.
 * @returns {Function} - function.
 */
export const partial =
    (func, ...args) =>
    (...furtherArgs) =>
        func(...args, ...furtherArgs);
